import { userSpaceBounds, withPaint } from '@ck';
import type { Color, ColorMode } from '@color';
import { LineSegment, Point, type Matrix, type Rect } from '@geom';
import { ObjectFit, calculateObjectFitLayout, type Image } from '@image';
import { canvasKit } from '@resources';
import { Paragraph } from '@typography';
import { assertDefined } from '@utils';
import type { Canvas, Surface } from 'canvaskit';
import {
  isUniformCornerRadius,
  isZeroCornerRadius,
  normalizeCornerRadius,
  type CornerRadiusOpts,
  type NormalizedCornerRadiusOpts
} from './cornerRadius';
import type { Shape } from './Shape';
import {
  PaintStyle,
  Visual,
  VisualPaint,
  withVisualPaint,
  type DrawImageOpts,
  type DrawRectOpts,
  type VisualGroupOpts,
  DEFAULT_DPR
} from './Visual';
import { VisualCache } from './VisualCache';

export class CKVisual extends Visual {
  canvas: Canvas;
  surface: Surface;

  constructor(
    colorMode: ColorMode,
    cache: VisualCache,
    canvas: Canvas,
    surface: Surface,
    devicePixelRatio = DEFAULT_DPR
  ) {
    super(colorMode, cache, devicePixelRatio);
    this.canvas = canvas;
    this.surface = surface;
    this.scale(devicePixelRatio);
  }

  clear(color: Color): void {
    this.canvas.clear(color.ckColor);
  }

  save(): void {
    this.canvas.save();
  }

  restore(): void {
    this.canvas.restore();
  }

  translate(p: Point): void {
    this.canvas.translate(p.x, p.y);
  }

  scale(value: number): void {
    this.canvas.scale(value, value);
  }

  concat(matrix: Matrix): void {
    this.canvas.concat(matrix.elements);
  }

  userSpaceBounds(): { bounds: Rect; scale: number } {
    return userSpaceBounds(this.canvas, this.surface);
  }

  drawLine(line: LineSegment, paint: VisualPaint | undefined): void {
    assertDefined(paint);
    this.canvas.drawLine(line.p1.x, line.p1.y, line.p2.x, line.p2.y, paint.ckPaint);
  }

  drawRect(r: Rect, opts?: DrawRectOpts): void {
    const { fill, stroke, cornerRadius } = opts ?? {};
    const canvas = this.canvas;
    withVisualPaint(paint => {
      function draw() {
        if (cornerRadius !== undefined) {
          const cr = normalizeCornerRadius(cornerRadius);
          if (isZeroCornerRadius(cr)) {
            canvas.drawRect(r.typedArray, paint.ckPaint);
          } else if (isUniformCornerRadius(cr)) {
            const rx = cr.topLeft.width;
            const ry = cr.topLeft.height;
            const roundedRect = canvasKit.RRectXY(r.typedArray, rx, ry);
            canvas.drawRRect(roundedRect, paint.ckPaint);
          } else {
            const rr = rrectFromRect(r, cr);
            canvas.drawRRect(rr, paint.ckPaint);
          }
        } else {
          canvas.drawRect(r.typedArray, paint.ckPaint);
        }
      }

      if (fill && !fill.isTransparent) {
        paint.color = fill;
        paint.style = PaintStyle.FILL;
        draw();
      }

      if (stroke && !stroke.color.isTransparent && stroke.width > 0) {
        paint.color = stroke.color;
        paint.style = PaintStyle.STROKE;
        paint.strokeWidth = stroke.width;
        draw();
      }
    });
  }

  drawCircle(center: Point, radius: number, paint: VisualPaint): void {
    this.canvas.drawCircle(center.x, center.y, radius, paint.ckPaint);
  }

  drawShape(shape: Shape, paint: VisualPaint): void {
    this.canvas.drawPath(shape.ckPath, paint.ckPaint);
  }

  drawParagraph(paragraph: Paragraph, bounds: Rect, point: Point, opacity: number): void {
    const canvas = this.canvas;
    if (opacity < 1) {
      withPaint(paint => {
        paint.setColor(canvasKit.TRANSPARENT);
        paint.setAlphaf(opacity);
        canvas.saveLayer(paint, bounds.ckRect);
        canvas.drawParagraph(paragraph.ckParagraph, point.x, point.y);
        this.restore();
      });
    } else {
      canvas.drawParagraph(paragraph.ckParagraph, point.x, point.y);
    }
  }

  drawImage(image: Image, bounds: Rect, opts?: DrawImageOpts): void {
    const { objectFit = ObjectFit.FILL, objectPosition, opacity = 1.0 } = opts ?? {};

    const layout = calculateObjectFitLayout(
      image.size,
      bounds.size,
      objectFit,
      objectPosition
    );

    const canvas = this.canvas;

    if (opacity < 1) {
      withPaint(paint => {
        paint.setColor(canvasKit.TRANSPARENT);
        paint.setAlphaf(opacity);
        canvas.saveLayer(paint, bounds.ckRect);

        // Translate to bounds position and draw
        canvas.save();
        canvas.translate(bounds.left, bounds.top);
        canvas.drawImageRect(
          image.ckImage,
          layout.srcRect.typedArray,
          layout.destRect.typedArray,
          paint
        );
        canvas.restore();

        this.restore();
      });
    } else {
      withPaint(paint => {
        // Translate to bounds position and draw
        canvas.save();
        canvas.translate(bounds.left, bounds.top);
        canvas.drawImageRect(
          image.ckImage,
          layout.srcRect.typedArray,
          layout.destRect.typedArray,
          paint
        );
        canvas.restore();
      });
    }
  }

  withGroup(
    opts: Partial<VisualGroupOpts> | undefined,
    fn: (paint: VisualPaint | undefined) => void
  ): void {
    const opacity = opts?.opacity;
    const hasOpacity = opacity !== undefined && opacity < 1.0;
    const offset = opts?.offset;
    const hasOffset = offset !== undefined && !offset.equals(Point.ZERO);

    if (hasOpacity) {
      // Use saveLayer for opacity less than 1.0
      const canvas = this.canvas;
      withPaint(alphaPaint => {
        alphaPaint.setAlphaf(opacity);
        canvas.saveLayer(alphaPaint, null);
      });
    }

    if (hasOffset) {
      this.canvas.save();
      this.canvas.translate(offset.x, offset.y);
    }

    withVisualPaint(paint => {
      if (opts) {
        if (opts.color) {
          paint.color = opts.color;
        }
        if (opts.strokeWidth) {
          paint.strokeWidth = opts.strokeWidth;
        }
      }
      fn(paint);
    });

    if (hasOffset) {
      this.canvas.restore();
    }

    if (hasOpacity) {
      // Restore the layer to apply the opacity
      this.canvas.restore();
    }
  }

  withClip(
    bounds: Rect,
    fn: () => void,
    cornerRadius?: number | Partial<CornerRadiusOpts>
  ): void {
    const canvas = this.canvas;

    // Save the current canvas state
    canvas.save();

    try {
      // Set up clipping based on corner radius
      if (cornerRadius !== undefined) {
        const cr = normalizeCornerRadius(cornerRadius);
        if (isZeroCornerRadius(cr)) {
          canvas.clipRect(bounds.typedArray, canvasKit.ClipOp.Intersect, true);
        } else if (isUniformCornerRadius(cr)) {
          const rx = cr.topLeft.width;
          const ry = cr.topLeft.height;
          const roundedRect = canvasKit.RRectXY(bounds.typedArray, rx, ry);
          canvas.clipRRect(roundedRect, canvasKit.ClipOp.Intersect, true);
        } else {
          const rr = rrectFromRect(bounds, cr);
          canvas.clipRRect(rr, canvasKit.ClipOp.Intersect, true);
        }
      } else {
        canvas.clipRect(bounds.typedArray, canvasKit.ClipOp.Intersect, true);
      }

      // Execute the function with clipping active
      fn();
    } finally {
      // Always restore the canvas state
      canvas.restore();
    }
  }
}

export function rrectFromRect(
  rect: Rect,
  radius: NormalizedCornerRadiusOpts
): Float32Array {
  return Float32Array.of(
    rect.left,
    rect.top,
    rect.right,
    rect.bottom,
    radius.topLeft.width,
    radius.topLeft.height,
    radius.topRight.width,
    radius.topRight.height,
    radius.bottomRight.width,
    radius.bottomRight.height,
    radius.bottomLeft.width,
    radius.bottomLeft.height
  );
}
